// src/components/UploadConLoading.jsx
import React, { useRef, useState, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../utils/axiosConfig";
import { FiUploadCloud, FiFileText } from "react-icons/fi";
import { FormGroup, Input, Label } from "reactstrap";

export default function UploadConLoading({ onClose }) {
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null); // para img/pdf
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [nombreTemplate, setNombreTemplate] = useState("");
    const controllerRef = useRef(null);
    const inputRef = useRef(null);

    // Config
    const uploadUrl = `${process.env.REACT_APP_SERVER}/operaciones/uploadplantilla`;
    const fieldName = "file";
    const accept = "*/*";

    useEffect(() => () => controllerRef.current?.abort(), []);

    // generar / limpiar preview
    useEffect(() => {
        if (!file) { setPreviewUrl(null); return; }
        const canPreview =
            file.type?.startsWith("image/") || file.type === "application/pdf";
        if (!canPreview) { setPreviewUrl(null); return; }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const bytesToReadable = (b) => {
        if (!Number.isFinite(b)) return "";
        const u = ["B", "KB", "MB", "GB"]; let i = 0, n = b;
        while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
        return `${n.toFixed(n >= 100 ? 0 : n >= 10 ? 1 : 2)} ${u[i]}`;
    };

    const openPicker = () => inputRef.current?.click();
    const handleFileChange = (e) => setFile(e.target.files?.[0] ?? null);

    // drag & drop
    const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
    const onDragEnter = (e) => { e.preventDefault(); setDragActive(true); };
    const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
    const onDrop = (e) => {
        e.preventDefault(); setDragActive(false);
        const f = e.dataTransfer.files?.[0]; if (f) setFile(f);
    };

    const resetForm = () => { setFile(null); inputRef.current && (inputRef.current.value = ""); };

    // subir (solo loading; cierra si success === true)
    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) { Swal.fire("Archivo requerido", "Selecciona un archivo primero.", "warning"); return; }

        const fd = new FormData(); fd.append(fieldName, file);
        const controller = new AbortController(); controllerRef.current = controller;

        setIsUploading(true);
        Swal.fire({ title: "Subiendo archivo…", allowOutsideClick: false, didOpen: () => Swal.showLoading(), showConfirmButton: false });

        try {
            const resp = await api.post(uploadUrl, fd, {
                headers: { Authorization: `Bearer ${localStorage.getItem("tkn") || ""}` },
                signal: controller.signal,
                transformRequest: [(d) => d],
            });

            if (resp?.data?.success === true) {
                Swal.close();
                resetForm();
                onClose?.();
                return;
            }
            Swal.close();
            Swal.fire("Error", resp?.data?.message || "La API no confirmó la subida.", "error");
        } catch (err) {
            Swal.close();
            Swal.fire("Error", err?.response?.data?.message || err?.message || "Error al subir.", "error");
        } finally {
            setIsUploading(false);
            controllerRef.current = null;
        }
    };

    const handleCancel = () => { if (isUploading) return; resetForm(); onClose?.(); };

    // helper para extensión de archivos no previsualizables
    const getExt = (name = "") => (name.includes(".") ? name.split(".").pop()?.toUpperCase() : "FILE");

    return (
        <form onSubmit={handleUpload} className="w-full max-w-4xl mx-auto">
            <div className="bg-white  p-6">
                <div className="item span-6">
                    <FormGroup className="mb-0">
                        <Label htmlFor="Latitud" className="mb-1 text-uppercase small fw-semibold">Nombre </Label>
                        <Input id="Latitud" type="text" step="any" bsSize="sm" value={nombreTemplate} onChange={(e) => setNombreTemplate(e.target.value)} style={{ boxSizing: "border-box", height: "38px" }} />
                    </FormGroup>
                </div>
                {/* Título */}
                <div className="mb-5">
                    <p className="text-sm text-slate-500">Selecciona o arrastra el archivo.</p>
                </div>

                {/* DISTRIBUCIÓN HORIZONTAL */}
                <div className="flex flex-col md:flex-row gap-6">
                    {/* Dropzone con PREVIEW */}
                    <div className="md:w-2/3">
                        <div
                            onDragOver={onDragOver}
                            onDragEnter={onDragEnter}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            onClick={openPicker}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPicker()}
                            className={[
                                "relative flex flex-col items-center justify-center rounded-xl cursor-pointer",
                                "border border-dashed p-5 text-center transition",
                                dragActive ? "border-indigo-500 bg-indigo-50/50" : "border-slate-300 hover:bg-slate-50"
                            ].join(" ")}
                            style={{ minHeight: 180 }}
                        >
                            {/* PREVIEW */}
                            {file ? (
                                previewUrl ? (
                                    // imagen o pdf
                                    file.type.startsWith("image/") ? (
                                        <img
                                            src={previewUrl}
                                            alt="preview"
                                            className="max-h-40 object-contain rounded-md"
                                        />
                                    ) : (
                                        <iframe
                                            title="preview"
                                            src={previewUrl}
                                            className="w-full h-40 rounded-md"
                                        />
                                    )
                                ) : (
                                    // genérico (xlsx, docx, etc.)
                                    <div className="flex flex-col items-center justify-center">
                                        <FiFileText size={22} className="text-slate-400 mb-1" />
                                        <span className="text-xs text-slate-500">Vista previa no disponible ({getExt(file.name)})</span>
                                    </div>
                                )
                            ) : (
                                <>
                                    <FiUploadCloud size={22} className="mb-1.5 text-slate-400" aria-hidden="true" />
                                    <span className="text-sm font-medium text-indigo-600">Seleccionar archivo</span>
                                    <span className="text-[11px] text-slate-500 mt-0.5">o arrastra y suelta aquí</span>
                                </>
                            )}

                            {/* input file oculto */}
                            <input
                                ref={inputRef}
                                type="file"
                                accept={accept}
                                onChange={handleFileChange}
                                className="hidden"
                                style={{ display: "none" }}
                            />
                        </div>
                    </div>

                    {/* Panel derecho: info del archivo */}
                    <div className="md:w-1/3">
                        {file ? (
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-sm font-medium text-slate-900 break-words">{file.name}</p>
                                <p className="mt-1 text-xs text-slate-500">{bytesToReadable(file.size)}</p>
                                <button
                                    type="button"
                                    onClick={() => setFile(null)}
                                    disabled={isUploading}
                                    className="mt-3 inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-white disabled:opacity-50"
                                >
                                    Quitar
                                </button>
                            </div>
                        ) : (
                            <div className="rounded-lg border p-3 text-xs text-slate-500">
                                Ningún archivo seleccionado.
                            </div>
                        )}
                    </div>
                </div>

                {/* ACCIONES en la MISMA LÍNEA */}
                <div className="mt-6 pt-4 flex flex-row items-center" style={{ justifyContent: "center", alignContent: "center", display: "flex" }}>
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isUploading}
                        style={{ minWidth: 64 }}
                    >
                        Cerrar
                    </button>
                    <button
                        type="submit"
                        disabled={!file || isUploading}
                        className="btn btn-primary btn-sm"
                    >
                        {isUploading ? "Subiendo…" : "Subir"}
                    </button>
                </div>
            </div>
        </form>
    );
}
